import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { BedDouble, Save, IndianRupee } from "lucide-react";

/**
 * Defaults for the 3 one-time hostel fees applied at enrolment.
 * These get pre-filled inside the enrolment modal when "Add dormitory bed" is toggled on.
 * Staff can still edit per enrolment.
 */
export default function AdminHostelSettings() {
  const [form, setForm] = useState({ registration_fee: "", admission_fee: "", security_deposit: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/hostel-settings");
      setForm({
        registration_fee: String(data.registration_fee ?? ""),
        admission_fee: String(data.admission_fee ?? ""),
        security_deposit: String(data.security_deposit ?? ""),
      });
      setUpdatedAt(data.updated_at);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/hostel-settings", {
        registration_fee: parseFloat(form.registration_fee) || 0,
        admission_fee: parseFloat(form.admission_fee) || 0,
        security_deposit: parseFloat(form.security_deposit) || 0,
      });
      toast.success("Hostel one-time fee defaults saved");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-white/50">Loading hostel settings…</div>;

  const setField = (name, v) => setForm((f) => ({ ...f, [name]: v }));

  return (
    <div data-testid="admin-hostel-settings">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Hostel · One-time fee defaults</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-2 flex items-center gap-3">
        <BedDouble className="brand-orange" size={36} /> Hostel Settings.
      </h1>
      <p className="text-sm text-white/60 max-w-2xl mb-8">
        These amounts are auto-filled inside the enrolment modal whenever a dormitory bed is added.
        You (or the desk staff) can still tweak them per enrolment — this page just sets the standing defaults.
      </p>

      <form onSubmit={save} className="max-w-2xl bg-[#131A2B] border border-white/12 p-6 space-y-5">
        <FeeField label="Registration Fee (one-time · non-refundable)" name="registration_fee"
                  value={form.registration_fee} onChange={(v) => setField("registration_fee", v)}
                  hint="One-time hostel registration processing charge." />
        <FeeField label="Admission Fee (one-time · non-refundable)" name="admission_fee"
                  value={form.admission_fee} onChange={(v) => setField("admission_fee", v)}
                  hint="Annual hostel admission fee." />
        <FeeField label="Security Deposit (refundable)" name="security_deposit"
                  value={form.security_deposit} onChange={(v) => setField("security_deposit", v)}
                  hint="Refundable at exit, subject to no dues and property clearance." />

        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          <div className="text-[11px] font-mono text-white/40">
            {updatedAt ? `Last updated ${new Date(updatedAt).toLocaleString()}` : "Never updated"}
          </div>
          <button type="submit" disabled={saving}
            data-testid="hostel-settings-save"
            className="bg-brand-orange text-black px-6 py-2.5 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-white disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : "Save defaults"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FeeField({ label, name, value, onChange, hint }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{label}</div>
      <div className="flex items-center border border-white/20 focus-within:border-brand-orange bg-[#0B1120]">
        <span className="pl-3 pr-2 text-white/40"><IndianRupee size={14} /></span>
        <input
          type="number" min="0" step="1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`hostel-${name}`}
          className="w-full bg-transparent py-3 pr-3 font-mono text-sm outline-none"
        />
      </div>
      {hint && <div className="mt-1.5 text-[11px] text-white/50">{hint}</div>}
    </label>
  );
}
